import * as React from 'react';

import classnames from 'classnames';
import _ from 'underscore';

import {TimesCircleIcon, TickCircleIcon} from '../icon';

import styles from './regular-looking-textfield.module.css';

type Props = {
    name: string;
    value: string | number;
    debounce?: number;
    autoFocus?: boolean;
    disabled?: boolean;
    autocomplete?: boolean;
    isValid?: boolean;
    multiline?: boolean;
    persistActionsOnBlur?: boolean;
    required?: boolean;
    showActions?: boolean;
    placeholder?: string;
    minHeight?: number;
    paddingBottom?: number;
    textFieldRef?: (arg1?: HTMLElement | null | undefined) => void;
    onBlur: (arg1: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onCancel: () => void;
    onChange: (newValue: string, e: React.SyntheticEvent<any>) => void;
    onConfirm: () => void;
    onFocus?: (arg1: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

type State = {
    hasFocus: boolean;
    isActionsMenu: boolean;
    value?: string | number;
};

export class RegularLookingTextField extends React.Component<Props, State> {
    inputRef: HTMLInputElement | null | undefined | HTMLTextAreaElement | null | undefined;

    static defaultProps = {
        value: '',

        disabled: false,
        autoFocus: false,
        isValid: true,
        multiline: false,
        persistActionsOnBlur: false,
        showActions: false,
        required: false,

        onBlur: () => {},
        onCancel: () => {},
        onConfirm: () => {},
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            hasFocus: false,
            isActionsMenu: false,
            value: props.value,
        };
    }

    componentDidMount() {
        const inputRef = this.inputRef;
        if (this.props.autoFocus && !this.props.disabled && inputRef) {
            inputRef.focus();
            inputRef.setSelectionRange(inputRef.value.length, inputRef.value.length);
        }

        this.setPaddingBottom();
        this.setMinHeight();

        if (this.props.multiline && this.inputRef) {
            this.resizeTextArea(this.props.value);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.value !== this.state.value) {
            this.setState({value: nextProps.value});
        }
    }

    render() {
        const NodeType = this.props.multiline ? 'textarea' : 'input';
        const isActive = this.props.value || this.props.value === 0 || this.state.hasFocus;

        const styleName = classnames({
            [styles[NodeType]]: !isActive,
            [styles[`${NodeType}--active`]]: isActive,
            [styles['input--invalid']]: !this.props.isValid,
        });

        return (
            <>
                <NodeType
                    name={this.props.name}
                    value={this.props.debounce ? this.state.value || '' : this.props.value || ''}
                    required={this.props.required}
                    ref={this.handleRefAssignment}
                    disabled={this.props.disabled}
                    autoComplete={this.props.autocomplete ? 'on' : 'off'}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    onFocus={this.handleFocus}
                    placeholder={this.props.placeholder}
                    className={styleName}
                />
                {this.renderActionButtons()}
            </>
        );
    }

    renderActionButtons = () => {
        const {showActions} = this.props;

        if (!showActions) {
            return null;
        }

        const styleName =
            this.state.isActionsMenu && showActions
                ? 'actions-container--active'
                : 'actions-container';

        return (
            <div className={styleName}>
                <button
                    onMouseDown={this.handleMouseDownConfirmButton}
                    tabIndex={-1}
                    type='button'
                    className={styles['button-confirm']}
                >
                    <TickCircleIcon style={{height: 20}} size={20} />
                </button>
                {this.props.required ? undefined : (
                    <button
                        onMouseDown={this.handleMouseDownCancelButton}
                        tabIndex={-1}
                        type='button'
                        className={styles['button-cancel']}
                    >
                        <TimesCircleIcon />
                    </button>
                )}
            </div>
        );
    };

    // @ts-expect-error
    handleChangeDebounced = _.debounce((newValue, e) => {
        this.props.onChange(newValue, e);
    }, this.props.debounce || 0);

    handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (this.props.debounce) {
            this.setState({value: e.target.value});
            this.handleChangeDebounced(e.target.value, e);
        } else {
            this.props.onChange(e.target.value, e);
        }

        if (this.props.multiline) {
            this.resizeTextArea(e.target.value);
        }
    };

    handleFocus = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // When tabbing into the field, the text starts out selected.  This is normally fine, but breaks
        // clicking on canned options of the date-time picker.  We're using persistActionsOnBlur as a hacky
        // check whether this is a date-time-picker field, since that's the only time it's true.
        if (this.inputRef && this.props.persistActionsOnBlur) {
            this.inputRef.selectionEnd = this.inputRef.selectionStart;
        }

        this.setState({
            hasFocus: true,
            isActionsMenu: true,
        });
        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    handleBlur = (e: React.ChangeEvent<any>) => {
        this.setState({
            // @ts-expect-error
            isActionsMenu: this.props.persistActionsOnBlur,
            hasFocus: false,
        });
        this.props.onBlur(e);
    };

    handleMouseDownConfirmButton = () => {
        this.setState({isActionsMenu: false}, this.props.onConfirm);
    };

    handleMouseDownCancelButton = (e: React.SyntheticEvent<any>) => {
        this.props.onChange('', e);

        this.setState(
            {
                isActionsMenu: false,
                value: '',
            },
            this.props.onCancel
        );
    };

    handleRefAssignment = (
        node: HTMLInputElement | null | undefined | HTMLTextAreaElement | null | undefined
    ) => {
        this.inputRef = node;
        if (this.props.textFieldRef) {
            this.props.textFieldRef(node);
        }
    };

    resizeTextArea = (fieldValue: string | number) => {
        if (!this.inputRef) return;
        if (fieldValue) {
            this.inputRef.style.height = 'auto';
            this.inputRef.style.height = `${this.inputRef.scrollHeight + 5}px`;
        } else {
            this.inputRef.style.removeProperty('height');
        }
    };

    setMinHeight = () => {
        if (!this.inputRef || !this.props.minHeight) return;
        this.inputRef.style.minHeight = `${this.props.minHeight}px`;
    };

    setPaddingBottom = () => {
        if (!this.inputRef || !this.props.paddingBottom) return;
        this.inputRef.style.paddingBottom = `${this.props.paddingBottom}px`;
    };

    focus = () => {
        if (this.inputRef) this.inputRef.focus();
    };

    blur = () => {
        if (this.inputRef) this.inputRef.blur();
    };
}
